import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Wrench, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWsEvent } from "@/context/WebSocketContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaintenanceGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site/settings`);
      setSettings(data);
    } catch {
      setSettings({ maintenance_enabled: false });
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Real-time update via WebSocket
  useWsEvent("settings_updated", () => { load(); }, [load]);

  const enabled = !!settings?.maintenance_enabled;
  const isAdmin = user && user.role === "admin";
  // Always allow login + admin route through so admins can still recover
  const isAuthRoute = ["/login", "/register", "/admin"].some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  if (!enabled || isAdmin || isAuthRoute) {
    return (
      <>
        {enabled && isAdmin && (
          <div
            data-testid="maintenance-admin-banner"
            className="fixed top-16 sm:top-20 left-1/2 -translate-x-1/2 z-50 px-3"
          >
            <div className="glass-strong rounded-full px-4 py-2 border border-[#F59E0B]/50 flex items-center gap-2 shadow-lg">
              <Wrench size={13} className="text-[#F59E0B]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] font-bold">
                Wartungsmodus aktiv · nur Admins sehen die Seite
              </span>
            </div>
          </div>
        )}
        {children}
      </>
    );
  }

  return (
    <div
      data-testid="maintenance-gate"
      className="min-h-screen bg-[#020B1F] text-white grid place-items-center px-5"
    >
      <div className="relative max-w-2xl w-full">
        <div className="pointer-events-none absolute inset-0 -z-0">
          <div className="glow-orb glow-orb-neon animate-orb" style={{ width: 500, height: 500, top: "-20%", left: "-20%", opacity: 0.25 }} />
          <div className="glow-orb glow-orb-deep animate-orb" style={{ width: 600, height: 600, bottom: "-30%", right: "-25%", opacity: 0.3, animationDelay: "3s" }} />
        </div>
        <div className="glass-strong rounded-3xl p-8 sm:p-12 border border-[#00AFFF]/30 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 mb-6">
            <Wrench size={26} className="text-[#F59E0B]" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#00AFFF] mb-3">Eiffel 65 · Maintenance</div>
          <h1 className="font-display font-black text-3xl sm:text-5xl silver-text mb-4">
            {settings.maintenance_title || "Wir sind gleich zurück"}
          </h1>
          <p className="text-white/70 font-body text-sm sm:text-base leading-relaxed max-w-xl mx-auto whitespace-pre-line">
            {settings.maintenance_message || "Die Eiffel 65 Fan Community pflegt gerade ein paar Songs."}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.4em] text-white/40">
            <ShieldCheck size={11} /> Status wird automatisch aktualisiert
          </div>
        </div>
      </div>
    </div>
  );
}
